import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Clock, BadgeCheck, XCircle, ShieldAlert, Check, FileText, ShoppingBag, ArrowUpRight, ArrowRight, Download, FolderLock, FolderKanban, ShieldCheck, Video } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { StatusPill } from "../components/AccountShell";
import { RewardsPanel } from "../components/RewardsPanel";
import { OrderTimeline } from "../components/OrderTimeline";
import { OrderRetryPay } from "../components/OrderRetryPay";
import { Badge } from "../components/Badge";
import { VerifiedPartnerBadge } from "../components/VerifiedBadge";
import { MyPriceAlerts } from "../components/MyPriceAlerts";
import { OrderClaimButton } from "../components/OrderClaimButton";
import { SponsorInbox } from "../components/SponsorInbox";
import { SponsorClaimInbox } from "../components/SponsorClaimInbox";
import { SponsorDependentsPortfolio } from "../components/SponsorDependentsPortfolio";

const LEVEL2 = new Set(["introducer", "custodian", "guarantor"]);

const DependentSponsorBanner = ({ user }) => {
  const parentBiz = user?.parent_business_name || user?.parent_category
    ? (user.parent_business_name || `your ${user.parent_category}`)
    : "your sponsor";
  return (
    <div data-testid="dependent-sponsor-banner"
         className="mt-8 rounded-sm border border-[#D8B23E]/40 bg-gradient-to-r from-[#D8B23E]/[0.08] via-[#B87333]/[0.05] to-transparent p-5 flex flex-wrap items-start gap-4">
      <div className="h-10 w-10 rounded-sm bg-[#D8B23E]/20 text-[#D8B23E] grid place-items-center shrink-0">
        <span className="font-grotesk text-[10px] uppercase tracking-[0.28em]">Gate</span>
      </div>
      <div className="flex-1 min-w-[240px]">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Sponsor-gated account</div>
        <div className="font-serif text-xl text-[#F5F5F0] mt-1">
          Every activity is co-signed by <strong>{parentBiz}</strong>
        </div>
        <p className="font-body text-[13px] text-[#B8B8B0] mt-1 max-w-3xl leading-relaxed">
          Because you're mapped under a <strong>{user.parent_category || "Level-2 sponsor"}</strong>, each order, claim and material request you raise
          is first routed to their inbox for approval, then to the Vinci trade desk. Nothing hits Admin until your sponsor signs off.
        </p>
      </div>
    </div>
  );
};
import { SponsorInvites } from "../components/SponsorInvites";
import { CustodianLedger } from "../components/CustodianLedger";
import { MyBuyerQuotations } from "../components/MyBuyerQuotations";
import { MyCustodianCard } from "../components/MyCustodianCard";
import { MySRNs } from "../components/MySRNs";
import { BillDeskTile } from "../components/BillDeskTile";
import { MeetingsPanel } from "../components/Meetings";
import { VirtualMeetingButton } from "../components/VirtualMeetingButton";
import { BigCapGateway } from "../components/BigCapGateway";
import { PurchaseOrderButton } from "../components/PurchaseOrderButton";
import { CreditLimitApplication } from "../components/CreditLimitApplication";
import { EliteBadge } from "../components/EliteBadge";
import { InvoicesWidget } from "../components/InvoicesWidget";
import { VinciTerminalHero } from "../components/VinciTerminalHero";
import { WelcomeJitsiBanner } from "../components/WelcomeJitsiBanner";
import { TIER_BENEFITS } from "../lib/tiers";
import { BUYER_BADGES } from "../lib/badges";

function groupByProject(orders) {
  const map = new Map();
  for (const o of orders) {
    const key = (o.project_name || "").trim() || "Unassigned";
    const g = map.get(key) || { name: key, orders: [], total: 0 };
    g.orders.push(o);
    g.total += o.total || 0;
    map.set(key, g);
  }
  // tagged projects first (by value), Unassigned last
  return Array.from(map.values()).sort((a, b) => {
    if (a.name === "Unassigned") return 1;
    if (b.name === "Unassigned") return -1;
    return b.total - a.total;
  });
}
import { formatINR } from "../lib/format";

const PROFILE_LABELS = {
  business_name: "Business name",
  contact_person: "Contact person",
  gst_number: "GST number",
  pan: "PAN",
  phone: "Phone",
  address: "Address",
  category_info: "Category details",
  materials_supplied: "Materials supplied",
  supply_capacity: "Supply capacity",
  years_in_business: "Years in business",
  docs_note: "Compliance note",
};

const SellerBanner = ({ status }) => {
  const map = {
    pending: { Icon: Clock, color: "#D8B23E", title: "Application under review", text: "Your seller profile is being verified by the Vinci trade desk. You'll be marked as an Authorised Seller once approved." },
    approved: { Icon: BadgeCheck, color: "#2FB999", title: "You are an Authorised Seller", text: "Your account is verified. You can now be listed as an authorised supplier for Vinci Inc." },
    rejected: { Icon: XCircle, color: "#C0392B", title: "Application not approved", text: "Your application wasn't approved at this time. Please contact our trade desk for details." },
  };
  const cfg = map[status] || map.pending;
  return (
    <div data-testid="seller-status-banner" className="flex items-start gap-4 rounded-sm border px-5 py-5 mt-10" style={{ borderColor: `${cfg.color}44`, background: `${cfg.color}0D` }}>
      <cfg.Icon size={22} style={{ color: cfg.color }} className="shrink-0 mt-0.5" />
      <div>
        <div className="font-serif text-xl" style={{ color: cfg.color }}>{cfg.title}</div>
        <p className="font-body text-sm text-[#C9C9C2] mt-1 max-w-xl leading-relaxed">{cfg.text}</p>
      </div>
    </div>
  );
};

const BuyerVerifyBanner = () => (
  <div data-testid="buyer-verify-banner" className="flex items-start gap-4 rounded-sm border px-5 py-5 mt-10" style={{ borderColor: "#D8B23E44", background: "#D8B23E0D" }}>
    <ShieldAlert size={22} style={{ color: "#D8B23E" }} className="shrink-0 mt-0.5" />
    <div>
      <div className="font-serif text-xl" style={{ color: "#D8B23E" }}>Verification pending</div>
      <p className="font-body text-sm text-[#C9C9C2] mt-1 max-w-xl leading-relaxed">
        As a Super-Stockist, your account is under verification by the Vinci trade desk. Full tier pricing unlocks once verified.
      </p>
    </div>
  </div>
);

const TierBenefits = ({ category, verified }) => {
  const t = TIER_BENEFITS[category];
  const b = BUYER_BADGES[category];
  if (!t || !b) return null;
  return (
    <div className="mt-12 rounded-sm border border-white/10 overflow-hidden" data-testid="tier-benefits">
      <div className="p-8 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${b.from}22, ${b.to}0A)` }}>
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full blur-3xl opacity-30" style={{ background: b.glow }} />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-9 h-9 rounded-full text-black" style={{ background: `linear-gradient(135deg, ${b.from}, ${b.to})` }}>
              <b.Icon size={16} />
            </span>
            <span className="font-grotesk text-[11px] uppercase tracking-[0.2em]" style={{ color: b.to }}>{b.label} · {b.tier}</span>
          </div>
          <h3 className="font-serif text-3xl mt-5">{t.headline}</h3>
          <div className="font-body text-lg mt-2" style={{ color: b.to, filter: verified ? "none" : "blur(5px)" }}>
            {t.price}
          </div>
          {!verified && <p className="font-body text-xs text-[#8A8A82] mt-1">Pricing unlocks after verification.</p>}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-px bg-white/10">
        {t.perks.map((p) => (
          <div key={p} className="bg-[#070707] p-5 flex items-center gap-3">
            <Check size={16} style={{ color: b.to }} className="shrink-0" />
            <span className="font-body text-sm text-[#C9C9C2]">{p}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const WALLET_META = [
  { key: "paid", label: "Paid out", color: "#2FB999", statuses: ["paid"] },
  { key: "approved", label: "Approved (awaiting payout)", color: "#B87333", statuses: ["approved"] },
  { key: "review", label: "Under review", color: "#D8B23E", statuses: ["submitted", "under_review"] },
];

const CommissionWallet = ({ claims }) => {
  const list = claims || [];
  const sumFor = (statuses) => list.filter((c) => statuses.includes(c.status)).reduce((t, c) => t + (Number(c.amount_claimed) || 0), 0);
  const lifetime = sumFor(["approved", "paid"]);
  return (
    <div className="mt-12" data-testid="commission-wallet">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
        <h2 className="font-serif text-2xl md:text-3xl">Commission wallet</h2>
        <span className="font-body text-xs text-[#8A8A82]">Lifetime approved + paid: <span className="text-[#2FB999] font-body">{formatINR(lifetime)}</span> · {list.length} claim{list.length === 1 ? "" : "s"}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
        {WALLET_META.map((m) => {
          const amt = sumFor(m.statuses);
          const count = list.filter((c) => m.statuses.includes(c.status)).length;
          return (
            <div key={m.key} data-testid={`wallet-${m.key}`} className="bg-[#070707] p-7 relative overflow-hidden">
              <div className="absolute -right-6 -top-8 w-28 h-28 rounded-full blur-3xl opacity-20" style={{ background: m.color }} />
              <div className="relative z-10">
                <span className="font-grotesk text-[10px] uppercase tracking-[0.22em]" style={{ color: m.color }}>{m.label}</span>
                <div className="font-serif text-3xl md:text-4xl tracking-[-0.02em] mt-3">{formatINR(amt)}</div>
                <div className="font-body text-xs text-[#8A8A82] mt-2">{count} claim{count === 1 ? "" : "s"}</div>
              </div>
            </div>
          );
        })}
      </div>
      {list.length === 0 && (
        <p className="font-body text-sm text-[#8A8A82] mt-4" data-testid="wallet-empty">
          No claims yet. Submit a commission claim from your <Link to="/account/claims" className="text-[#B87333] hover:underline">Commission &amp; Incentives</Link> forms to start tracking your payouts here.
        </p>
      )}
    </div>
  );
};

const KycStatusBanner = ({ user }) => {
  // Founders / Directors never use the public Partner-Code page — their eKYC lives in the Founders Suite.
  const isDirector = user?.role === "director" || user?.account_type === "director";
  const kycHref = isDirector ? "/director/kyc" : "/verify";
  const st = user?.kyc_status || "not_started";
  if (st === "verified") {
    return (
      <div data-testid="kyc-banner-verified" className="flex items-start gap-4 rounded-sm border px-5 py-5 mt-10" style={{ borderColor: "#2FB99944", background: "#2FB9990D" }}>
        <BadgeCheck size={22} className="text-[#2FB999] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-serif text-xl text-[#2FB999]">eKYC complete · Account Verified</div>
          <p className="font-body text-sm text-[#C9C9C2] mt-1 max-w-xl leading-relaxed">Your identity is verified and agreements are signed.</p>
        </div>
        <Link to={kycHref} data-testid="kyc-view-agreements" className="shrink-0 self-center rounded-full border border-white/20 px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">View signed agreements</Link>
      </div>
    );
  }
  const failed = st === "failed";
  return (
    <div data-testid="kyc-banner-pending" className="flex items-start gap-4 rounded-sm border px-5 py-5 mt-10" style={{ borderColor: failed ? "#C0392B44" : "#B8733344", background: failed ? "#C0392B0D" : "#B873330D" }}>
      <ShieldAlert size={22} style={{ color: failed ? "#C0392B" : "#B87333" }} className="shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-serif text-xl" style={{ color: failed ? "#C0392B" : "#B87333" }}>{failed ? "eKYC needs your attention" : "Complete eKYC & sign agreements"}</div>
        <p className="font-body text-sm text-[#C9C9C2] mt-1 max-w-xl leading-relaxed">
          {isDirector
            ? "Upload your identity documents for your country of residence to complete Founders Suite eKYC."
            : failed
            ? "Your last verification didn't pass. Please re-check your PAN/GSTIN and sign again to get Verified."
            : "Verify your identity (PAN + GSTIN) and digitally sign your agreements to unlock full Verified access."}
        </p>
      </div>
      <Link to={kycHref} data-testid="kyc-start-btn" className="shrink-0 self-center rounded-full bg-[#F5F5F0] text-black px-6 py-3 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors">
        {failed ? "Retry verification" : "Verify now"}
      </Link>
    </div>
  );
};

export default function Account() {
  const { user, logout, api } = useAuth();
  const { add, setOpen: setCartOpen } = useCart();
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [claims, setClaims] = useState([]);
  const [docs, setDocs] = useState([]);
  const [signed, setSigned] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [dl, setDl] = useState(null);
  const [converting, setConverting] = useState(null);
  const [projectFilter, setProjectFilter] = useState("All");
  const [statementDl, setStatementDl] = useState(null);
  const [reordering, setReordering] = useState(null);

  const loadQuotes = useCallback(async () => {
    try { setQuotes((await api.get("/quote-requests/mine")).data); } catch { /* ignore */ }
  }, [api]);
  const loadOrders = useCallback(async () => {
    try { setOrders((await api.get("/orders/mine")).data); } catch { /* ignore */ }
  }, [api]);
  const loadClaims = useCallback(async () => {
    try { setClaims((await api.get("/claims/mine")).data); } catch { /* ignore */ }
  }, [api]);
  const loadDocs = useCallback(async () => {
    try { setDocs((await api.get("/documents/templates")).data); } catch { /* ignore */ }
  }, [api]);
  const loadSigned = useCallback(async () => {
    try {
      const { data } = await api.get("/kyc/status");
      setSigned(data);
    } catch { /* ignore */ }
  }, [api]);
  const loadRewards = useCallback(async () => {
    if (user?.account_type !== "buyer") return;
    try { setRewards((await api.get("/rewards/me")).data); } catch { /* ignore */ }
  }, [api, user]);
  useEffect(() => { loadQuotes(); loadOrders(); loadClaims(); loadDocs(); loadSigned(); loadRewards(); }, [loadQuotes, loadOrders, loadClaims, loadDocs, loadSigned, loadRewards]);

  const downloadTemplate = async (key, title) => {
    setDl(key);
    try {
      const res = await api.get(`/documents/templates/${key}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Vinci-${title.replace(/[^a-z0-9]+/gi, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the document."); }
    finally { setDl(null); }
  };

  const downloadInvoice = async (id) => {
    try {
      const res = await api.get(`/orders/${id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Vinci-Invoice-${id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download invoice."); }
  };

  const doLogout = async () => {
    await logout();
    toast.success("Signed out.");
    nav("/");
  };

  const addQuoteToCart = async (q) => {
    setConverting(q.id);
    try {
      const { data } = await api.get(`/quote-requests/${q.id}/match`);
      if (!data.product) {
        toast.error("No matching product in stock. Browse the store to pick one.");
        nav("/shop");
        return;
      }
      const p = data.product;
      const qty = Math.max(data.quantity || 1, p.moq || 1);
      add(p, qty, data.diameter || "");
      toast.success(`${p.name} × ${qty} added to cart`);
      setCartOpen(true);
    } catch {
      toast.error("Could not add to cart.");
    } finally {
      setConverting(null);
    }
  };

  const downloadStatement = async (project) => {
    setStatementDl(project);
    try {
      const res = await api.get("/orders/project-statement", { params: { project }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Vinci-Project-${project.replace(/[^a-z0-9]+/gi, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the project statement."); }
    finally { setStatementDl(null); }
  };

  const reorderProject = async (group) => {
    setReordering(group.name);
    try {
      const res = await api.get("/products");
      const products = Array.isArray(res.data) ? res.data : (res.data.products || []);
      const map = new Map(products.map((p) => [p.id, p]));
      const agg = new Map();
      for (const o of group.orders) for (const it of (o.items || [])) {
        agg.set(it.product_id, (agg.get(it.product_id) || 0) + (it.quantity || 0));
      }
      let added = 0, skipped = 0;
      for (const [pid, qty] of agg) {
        const p = map.get(pid);
        if (p && p.in_stock !== false) { add(p, Math.max(qty, p.moq || 1)); added += 1; }
        else skipped += 1;
      }
      if (added) { toast.success(`${added} item${added > 1 ? "s" : ""} added to cart${skipped ? ` · ${skipped} unavailable` : ""}`); setCartOpen(true); }
      else { toast.error("None of these items are currently available. Browse the store."); }
    } catch { toast.error("Could not reorder this project."); }
    finally { setReordering(null); }
  };

  const profile = user?.profile || {};
  const entries = Object.entries(PROFILE_LABELS).filter(([k]) => profile[k]);
  const isBuyer = user?.account_type === "buyer";

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0] px-6 md:px-12 py-16">
      <Toaster position="bottom-right" theme="dark" richColors />
      <div className="max-w-[1100px] mx-auto">
        <div className="flex items-center justify-between mb-12">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Vinci" className="h-7 w-auto" />
            <span className="font-serif text-xl">Vinci Inc.</span>
          </Link>
          <button data-testid="logout-btn" onClick={doLogout} className="rounded-full border border-white/20 px-5 py-2.5 font-grotesk text-xs uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">Sign out</button>
        </div>

        {/* Vinci Terminal · Glass Cockpit hero (buyer + seller only —
            admin has the Executive Command dashboard at /admin/command) */}
        {user?.role !== "admin" && <VinciTerminalHero api={api} />}

        <span className="overline text-[#B87333]">/ Your Account</span>
        <div className="flex flex-wrap items-center gap-5 mt-4">
          <h1 className="font-serif text-4xl md:text-6xl" data-testid="account-greeting">
            Welcome, {user?.name?.split(" ")[0] || "Partner"}.
          </h1>
          <Badge user={user} />
          <EliteBadge user={user} testid="account-elite-badge" />
          {user?.kyc_status === "verified" && <VerifiedPartnerBadge testid="account-verified-partner-badge" />}
          {isBuyer && user?.verified && (
            <span className="inline-flex items-center gap-1.5 text-[#2FB999] font-grotesk text-[11px] uppercase tracking-[0.18em]">
              <BadgeCheck size={15} /> Verified
            </span>
          )}
        </div>
        <p className="font-body text-[#8A8A82] max-w-lg mt-3">
          {user?.account_type === "seller" ? "Your Vinci authorised-seller profile." : "Your Vinci buyer profile, tier benefits and quote history."}
        </p>

        {user?.account_type === "seller" && <SellerBanner status={user.status} />}
        {isBuyer && !user?.verified && <BuyerVerifyBanner />}

        {isBuyer && user?.parent_id && LEVEL2.has(user?.parent_category) && (
          <DependentSponsorBanner user={user} />
        )}

        <KycStatusBanner user={user} />

        <WelcomeJitsiBanner api={api} />

        {isBuyer && <InvoicesWidget api={api} />}

        {/* Central admin-controlled video meeting request (any partner role) */}
        {user && user.role !== "admin" && (
          <div className="mt-8 border border-[#D8B23E]/25 rounded-sm p-5 bg-black/30 flex flex-wrap items-center justify-between gap-4" data-testid="virtual-meeting-strip">
            <div className="min-w-0">
              <div className="font-serif text-xl">Meet the Vinci Trade Desk</div>
              <p className="font-body text-[13px] text-[#8A8A82] mt-1">Book a private virtual conference on <b className="text-[#F5F5F0]">Jitsi</b> (preferred) — Zoho Meeting & Google Meet available on request. The desk authorises each request and sends the join link by email.</p>
            </div>
            <VirtualMeetingButton api={api} />
          </div>
        )}

        {/* Credit-limit application — Working-Capital programme */}
        {user && user.role !== "admin" && (
          <div className="mt-10" data-testid="credit-limit-section">
            <h2 className="font-serif text-2xl md:text-3xl mb-4">Credit Limit · Working-Capital Programme</h2>
            <CreditLimitApplication api={api} />
          </div>
        )}

        {isBuyer && <TierBenefits category={user.buyer_category} verified={user.verified} />}

        {isBuyer && <CommissionWallet claims={claims} />}

        {isBuyer && (
          <div className="mt-12 grid sm:grid-cols-2 gap-4" data-testid="buyer-claim-cards">
            <Link to="/account/claims" data-testid="claims-link" className="group rounded-sm border border-white/10 p-7 hover:border-[#B87333]/60 transition-colors relative overflow-hidden">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-[#B87333] text-black"><FileText size={18} /></span>
              <div className="font-serif text-2xl mt-5">Commission & Incentives</div>
              <p className="font-body text-sm text-[#8A8A82] mt-2 leading-relaxed">Claim your commission on direct & indirect orders. Track each claim's status.</p>
              <span className="inline-flex items-center gap-1 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] mt-5 group-hover:gap-2 transition-all">Open claim forms <ArrowUpRight size={13} /></span>
            </Link>
            <Link to="/shop" className="group rounded-sm border border-white/10 p-7 hover:border-white/25 transition-colors">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-[#3a3a35] text-[#F5F5F0]"><ShoppingBag size={18} /></span>
              <div className="font-serif text-2xl mt-5">Place a bulk order</div>
              <p className="font-body text-sm text-[#8A8A82] mt-2 leading-relaxed">Browse the catalogue and order at your tier price with state-wise GST invoicing.</p>
              <span className="inline-flex items-center gap-1 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#C9C9C2] mt-5 group-hover:gap-2 transition-all">Shop materials <ArrowUpRight size={13} /></span>
            </Link>
          </div>
        )}

        {user?.account_type === "seller" && (
          <div className="mt-12" data-testid="seller-quote-cards">
            <Link to="/account/quotations" data-testid="quotations-link" className="group block rounded-sm border border-white/10 p-7 hover:border-[#B87333]/60 transition-colors">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-[#B87333] text-black"><FileText size={18} /></span>
              <div className="font-serif text-2xl mt-5">Submit a quotation</div>
              <p className="font-body text-sm text-[#8A8A82] mt-2 leading-relaxed">Quote against a buyer requirement or an order demand raised by our trade desk. Track each quotation's status.</p>
              <span className="inline-flex items-center gap-1 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] mt-5 group-hover:gap-2 transition-all">Open quotation form <ArrowUpRight size={13} /></span>
            </Link>
          </div>
        )}

        {user?.account_type === "buyer" && <RewardsPanel data={rewards} />}

        {user?.status === "incomplete_onboarding" && (
          <Link to="/onboarding" data-testid="incomplete-banner"
            className="block mb-8 border border-[#B87333]/40 rounded-sm p-5 bg-gradient-to-r from-[#B87333]/[0.08] to-transparent hover:border-[#B87333] transition-colors">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="overline text-[#B87333] mb-1">Registration Incomplete</div>
                <div className="font-serif text-xl">Finish your Vinci master file</div>
                <div className="text-xs text-[#8A8A82] mt-1 font-body">Complete the profile + upload KYC documents to unlock trade, credit and the Ecosystem Certificate.</div>
              </div>
              <ArrowRight size={20} className="text-[#B87333] shrink-0" />
            </div>
          </Link>
        )}

        {/* Quick-access strip for role-specific dashboards */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="account-quick-nav">
          <Link to="/account/paid-quotes" data-testid="quick-nav-paid-quotes"
            className="group flex items-center gap-4 rounded-sm border border-[#B87333]/40 hover:border-[#B87333] transition-colors p-5 bg-[#B87333]/[0.04]">
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#B87333] text-black shrink-0"><FileText size={19} /></span>
            <div className="flex-1">
              <div className="font-serif text-xl">Create PDF Quote</div>
              <div className="font-body text-xs text-[#8A8A82] mt-1">Obsidian-themed quotation · ₹99 per quote</div>
            </div>
            <ArrowRight size={14} className="text-[#B87333]" />
          </Link>
          {user?.account_type === "buyer" && (
            <Link to="/account/statement" data-testid="quick-nav-statement"
              className="group flex items-center gap-4 rounded-sm border border-white/10 hover:border-[#B87333]/60 transition-colors p-5">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-[#B87333] text-black shrink-0"><FileText size={19} /></span>
              <div className="flex-1">
                <div className="font-serif text-xl">Account Statement</div>
                <div className="font-body text-xs text-[#8A8A82] mt-1">Ledger of orders, payments, refunds & credit</div>
              </div>
              <ArrowRight size={14} className="text-[#B87333]" />
            </Link>
          )}
          <Link to="/account/invoices" data-testid="quick-nav-invoices"
            className="group flex items-center gap-4 rounded-sm border border-white/10 hover:border-[#B87333]/60 transition-colors p-5">
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#D8B23E] text-black shrink-0"><FileText size={19} /></span>
            <div className="flex-1">
              <div className="font-serif text-xl">My Invoices</div>
              <div className="font-body text-xs text-[#8A8A82] mt-1">Download PDFs & pay open invoices in one tap</div>
            </div>
            <ArrowRight size={14} className="text-[#D8B23E]" />
          </Link>
          <div className="md:col-span-2">
            <BigCapGateway status={user?.big_cap?.status || "not_applied"} membershipNo={user?.big_cap?.membership_no}/>
          </div>
          {["introducer", "custodian", "guarantor"].includes((user?.buyer_category || "").toLowerCase()) && (
            <Link to="/account/deals" data-testid="quick-nav-deals"
              className="group flex items-center gap-4 rounded-sm border border-white/10 hover:border-[#D8B23E]/60 transition-colors p-5">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-[#B87333] text-black shrink-0"><FileText size={19} /></span>
              <div className="flex-1">
                <div className="font-serif text-xl">Deal Protection Desk</div>
                <div className="font-body text-xs text-[#8A8A82] mt-1">Register indirect deals · 180-day protection · commission auto-tracked</div>
              </div>
              <ArrowRight size={14} className="text-[#D8B23E]" />
            </Link>
          )}
          {user?.account_type === "seller" && (
            <>
              <Link to="/supplier/dashboard" data-testid="quick-nav-supplier-dashboard"
                className="group flex items-center gap-4 rounded-sm border border-white/10 hover:border-[#D8B23E]/60 transition-colors p-5">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-[#D8B23E] text-black shrink-0"><FileText size={19} /></span>
                <div className="flex-1">
                  <div className="font-serif text-xl">Seller Command Deck</div>
                  <div className="font-body text-xs text-[#8A8A82] mt-1">Daily rate pulse · commission slider · Elite scrutiny</div>
                </div>
                <ArrowRight size={14} className="text-[#D8B23E]" />
              </Link>
              <Link to="/supplier/license" data-testid="quick-nav-supplier-license"
                className="group flex items-center gap-4 rounded-sm border border-white/10 hover:border-[#B87333]/60 transition-colors p-5">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-[#B87333] text-black shrink-0"><FileText size={19} /></span>
                <div className="flex-1">
                  <div className="font-serif text-xl">License & Renewal</div>
                  <div className="font-body text-xs text-[#8A8A82] mt-1">Pay annual license · download GST receipt</div>
                </div>
                <ArrowRight size={14} className="text-[#D8B23E]" />
              </Link>
              <Link to="/supplier/settlement" data-testid="quick-nav-settlement"
                className="group flex items-center gap-4 rounded-sm border border-white/10 hover:border-[#B87333]/60 transition-colors p-5">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-[#2FB999] text-black shrink-0"><FileText size={19} /></span>
                <div className="flex-1">
                  <div className="font-serif text-xl">Settlement Desk</div>
                  <div className="font-body text-xs text-[#8A8A82] mt-1">Net earnings, commissions & payouts</div>
                </div>
                <ArrowRight size={14} className="text-[#2FB999]" />
              </Link>
              <Link to="/supplier/broadcasts" data-testid="quick-nav-broadcasts"
                className="group flex items-center gap-4 rounded-sm border border-white/10 hover:border-[#B87333]/60 transition-colors p-5">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-[#B87333] text-black shrink-0"><FileText size={19} /></span>
                <div className="flex-1">
                  <div className="font-serif text-xl">Open Requirements</div>
                  <div className="font-body text-xs text-[#8A8A82] mt-1">Anonymised RFQs curated by Vinci desk</div>
                </div>
                <ArrowRight size={14} className="text-[#B87333]" />
              </Link>
            </>
          )}
          <Link to="/account/meetings" data-testid="quick-nav-meetings"
            className="group flex items-center gap-4 rounded-sm border border-white/10 hover:border-[#B87333]/60 transition-colors p-5">
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#B87333] text-black shrink-0"><Video size={19} /></span>
            <div className="flex-1">
              <div className="font-serif text-xl">Meetings</div>
              <div className="font-body text-xs text-[#8A8A82] mt-1">Schedule secure Jitsi calls · one-click join</div>
            </div>
            <ArrowRight size={14} className="text-[#B87333]" />
          </Link>
        </div>

        <div className="mt-16" data-testid="document-vault">
          <Link
            to="/account/refund-compliance"
            data-testid="refund-compliance-tab"
            aria-label="Open Refund Compliance Desk"
            className="group flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-sm border border-[#C0392B]/40 hover:border-[#C0392B]/70 transition-colors p-5 md:p-6 mb-6 bg-gradient-to-r from-[#C0392B]/[0.06] via-transparent to-transparent"
          >
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#C0392B] text-white shrink-0">
              <ShieldAlert size={19} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-serif text-2xl md:text-3xl">Refund Compliance Desk</h2>
                <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#C0392B] border border-[#C0392B]/40 rounded-full px-2.5 py-0.5">
                  Audited · 30-day forensic
                </span>
              </div>
              <p className="font-body text-sm text-[#8A8A82] mt-1 leading-relaxed">
                The only channel for refund claims — 10-document audit trail, live video witness, OTP + no-claims certificate.
              </p>
            </div>
            <span className="inline-flex items-center gap-1 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#C0392B] group-hover:gap-2 transition-all self-start sm:self-center whitespace-nowrap">
              Open desk <ArrowRight size={13} />
            </span>
          </Link>

          <div className="mb-6">
            <MySRNs />
          </div>

          <BillDeskTile />

          <Link
            to="/account/documents"
            data-testid="document-vault-tab"
            aria-label="Open Document Vault"
            className="group flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-sm border border-white/10 hover:border-[#B87333]/60 transition-colors p-5 md:p-6 mb-6"
          >
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#B87333] text-black shrink-0">
              <FolderLock size={19} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-serif text-2xl md:text-3xl">Document Vault</h2>
                <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] border border-[#B87333]/40 rounded-full px-2.5 py-0.5">
                  {docs.length} template{docs.length === 1 ? "" : "s"}
                  {signed?.signatures?.length ? ` · ${signed.signatures.length} signed` : ""}
                </span>
              </div>
              <p className="font-body text-sm text-[#8A8A82] mt-1 leading-relaxed">
                Ready-to-use legal templates and every agreement you've signed with Vinci — open the full vault.
              </p>
            </div>
            <span className="inline-flex items-center gap-1 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] group-hover:gap-2 transition-all self-start sm:self-center whitespace-nowrap">
              Open vault <ArrowRight size={13} />
            </span>
          </Link>
          <p className="font-body text-sm text-[#8A8A82] mb-6 max-w-xl leading-relaxed">
            Below is a quick preview — tap "Open vault" above for the full grid, signed agreements and legal footnotes.
          </p>
          {docs.length === 0 ? (
            <p className="font-body text-sm text-[#8A8A82]" data-testid="vault-empty">No templates available for your account.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="vault-grid">
              {docs.map((d) => (
                <div key={d.key} data-testid={`vault-doc-${d.key}`} className="group rounded-sm border border-white/10 p-6 hover:border-[#B87333]/60 transition-colors flex flex-col">
                  <div className="flex items-start gap-4">
                    <span className="grid place-items-center w-11 h-11 rounded-full bg-[#141414] border border-white/10 text-[#B87333] shrink-0"><FileText size={18} /></span>
                    <div>
                      <div className="font-serif text-xl leading-snug">{d.title}</div>
                      <p className="font-body text-sm text-[#8A8A82] mt-2 leading-relaxed">{d.description}</p>
                    </div>
                  </div>
                  <button
                    data-testid={`vault-download-${d.key}`}
                    onClick={() => downloadTemplate(d.key, d.title)}
                    disabled={dl === d.key}
                    className="mt-5 self-start inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors disabled:opacity-50"
                  >
                    <Download size={13} /> {dl === d.key ? "Preparing…" : "Download PDF"}
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="font-body text-[10px] text-[#6f6f68] mt-4">Templates are for reference and become binding only upon execution by authorised signatories. Please seek independent legal advice.</p>

          {/* Signed agreements sub-section — certificate row filtered out (rendered
               with its lock/unlock state inside /account/documents). */}
          {signed && (signed.signatures || []).filter((s) => s.agreement_key !== "authorisation_certificate").length > 0 && (
            <div className="mt-10 pt-8 border-t border-white/10" data-testid="signed-agreements">
              <div className="flex items-center gap-3 mb-2">
                <span className="grid place-items-center w-9 h-9 rounded-full bg-[#2FB999] text-black"><ShieldCheck size={17} /></span>
                <h3 className="font-serif text-xl md:text-2xl">Your signed agreements</h3>
              </div>
              <p className="font-body text-sm text-[#8A8A82] mb-6 max-w-xl leading-relaxed">
                Every agreement you've electronically signed with Vinci — re-download anytime, share with your legal or finance team.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {signed.signatures.filter((s) => s.agreement_key !== "authorisation_certificate").map((s) => {
                  const isZoho = s.signature_type === "zoho";
                  const isSandbox = s.signature_type === "sandbox";
                  const backend = process.env.REACT_APP_BACKEND_URL;
                  const href = isZoho
                    ? `${backend}/api/kyc/zoho/signed/${s.reference}.pdf`
                    : isSandbox
                      ? `${backend}/api/kyc/sandbox/signed/${s.reference}.pdf`
                      : `${backend}/api/kyc/agreements/${s.agreement_key}/signed.pdf`;
                  const dateStr = s.signed_at ? new Date(s.signed_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";
                  return (
                    <a key={s.agreement_key} href={href} target="_blank" rel="noreferrer"
                      data-testid={`signed-${s.agreement_key}`}
                      className="group rounded-sm border border-white/10 p-5 hover:border-[#2FB999]/60 transition-colors flex items-start gap-4">
                      <span className="grid place-items-center w-11 h-11 rounded-full bg-[#2FB999]/[0.08] border border-[#2FB999]/60 text-[#2FB999] shrink-0"><ShieldCheck size={19} /></span>
                      <div className="flex-1 min-w-0">
                        <div className="font-serif text-lg leading-snug">{s.agreement_title}</div>
                        <div className="mt-1 flex items-center flex-wrap gap-2">
                          <span className="font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#2FB999]">Signed {dateStr}</span>
                          <span className="font-grotesk text-[9px] uppercase tracking-[0.15em] text-[#8A8A82] border border-white/15 rounded-full px-2 py-0.5">
                            {isZoho ? "Zoho eSign" : "In-house"}
                          </span>
                        </div>
                      </div>
                      <Download size={16} className="text-[#B87333] mt-2 group-hover:translate-y-[1px] transition-transform" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {user?.vendor_code && (
          <div
            data-testid="vendor-code-card"
            className="mt-16 relative overflow-hidden border border-[#B87333]/50 rounded-sm p-6 md:p-10"
            style={{
              background: "linear-gradient(135deg, #0a0806 0%, #0d0a07 40%, #08070a 100%)",
              boxShadow: "inset 0 0 90px rgba(184,115,51,0.14), 0 0 0 1px rgba(184,115,51,0.14), 0 20px 60px -20px rgba(0,0,0,0.8)",
            }}
          >
            {/* Corner marks — engraved credential vibe */}
            <span className="absolute top-3 left-3 w-6 h-6 border-t border-l border-[#B87333]/60" />
            <span className="absolute top-3 right-3 w-6 h-6 border-t border-r border-[#B87333]/60" />
            <span className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-[#B87333]/60" />
            <span className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-[#B87333]/60" />
            {/* Top hairline */}
            <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#B87333] to-transparent opacity-80" />
            {/* Ambient copper glow */}
            <div className="absolute -top-32 -right-32 w-72 h-72 rounded-full opacity-[0.10] pointer-events-none"
                 style={{ background: "radial-gradient(circle, #B87333 0%, transparent 70%)" }} />

            <div className="relative grid md:grid-cols-[1fr_auto] items-end gap-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-[1px] bg-[#B87333]" />
                  <span className="overline text-[#B87333] tracking-[0.32em] text-[10px]">Vinci Registry · Partner Credential</span>
                </div>
                <div
                  className="font-serif tabular-nums tracking-[0.06em] text-[#F5F5F0] leading-none"
                  data-testid="vendor-code-value"
                  style={{
                    fontSize: "clamp(2.75rem, 7vw, 5.5rem)",
                    background: "linear-gradient(180deg, #F5F5F0 0%, #E4C88E 55%, #B87333 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    filter: "drop-shadow(0 2px 12px rgba(184,115,51,0.25))",
                  }}
                >
                  {user.vendor_code}
                </div>
                <div className="mt-4 flex items-center flex-wrap gap-2">
                  <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1 rounded-sm border border-[#B87333]/45 text-[#B87333] bg-[#B87333]/[0.06]">
                    {(user.buyer_category || user.account_type || "Partner").replace(/_/g, " ").toUpperCase()}
                  </span>
                  {user.sub_role && (
                    <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-sm border border-white/20 text-[#C9C9C2] bg-white/[0.03]" data-testid="sub-role-line">
                      {user.sub_role === "independent" ? "3A · Independent"
                       : user.sub_role === "introducer_dependent" ? "3B · Introducer-Dependent"
                       : user.sub_role === "guarantor_dependent" ? "3C · Guarantor-Dependent"
                       : user.sub_role === "custodian_dependent" ? "3D · Custodian-Dependent"
                       : user.sub_role}
                    </span>
                  )}
                  {user.parent_business_name && (
                    <span className="font-body text-[11px] text-[#8A8A82]">
                      · sponsored by <b className="text-[#F5F5F0]">{user.parent_business_name}</b>
                      {user.parent_vendor_code && <span className="text-[#B87333]"> ({user.parent_vendor_code})</span>}
                    </span>
                  )}
                </div>
                <p className="font-body text-[11px] leading-relaxed text-[#8A8A82] mt-5 max-w-md">
                  Cite this code on every purchase order, quotation and invoice. It is your permanent identity across the Vinci partner network and the sole reference the desk needs to route your ledger.
                </p>
              </div>
              <div className="text-right font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] space-y-2 border-l border-[#B87333]/25 pl-6 md:pl-8">
                <div><span className="text-[#B87333]">5151</span> · Registry series</div>
                <div><span className="text-[#B87333]">{user.vendor_code.slice(-4)}</span> · Partner sequence</div>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="text-[#F5F5F0] font-serif normal-case text-sm tracking-normal">Issued by</div>
                  <div className="mt-1">Vinci Inc · Trade Desk</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <h2 className="font-serif text-2xl md:text-3xl mt-16 mb-6">Profile</h2>
        <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          <div className="bg-[#070707] p-8">
            <span className="overline text-[#8A8A82]">Login email</span>
            <div className="font-serif text-2xl mt-2 break-all">{user?.email}</div>
          </div>
          {entries.map(([k, label]) => (
            <div key={k} className="bg-[#070707] p-8">
              <span className="overline text-[#8A8A82]">{label}</span>
              <div className="font-serif text-2xl mt-2 break-words">{profile[k]}</div>
            </div>
          ))}
        </div>

        <MyPriceAlerts />
        <MyCustodianCard />
        <SponsorInbox />
        <SponsorDependentsPortfolio />
        <SponsorClaimInbox />
        <SponsorInvites />
        <CustodianLedger />
        <MeetingsPanel api={api} />

        {orders.some((o) => (o.project_name || "").trim()) && (
          <div data-testid="my-projects">
            <div className="flex items-center justify-between mt-16 mb-6">
              <h2 className="font-serif text-2xl md:text-3xl">My Projects <span className="text-[#8A8A82] text-lg">({groupByProject(orders).filter((g) => g.name !== "Unassigned").length})</span></h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {groupByProject(orders).map((g) => (
                <div key={g.name} data-testid={`project-group-${g.name.replace(/\s+/g, "-").toLowerCase()}`} className="border border-white/10 rounded-sm p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <FolderKanban size={16} className={g.name === "Unassigned" ? "text-[#6f6f68] shrink-0" : "text-[#B87333] shrink-0"} />
                      <span className="font-serif text-xl truncate">{g.name}</span>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-serif text-lg">{formatINR(g.total)}</div>
                      <div className="font-grotesk text-[9px] uppercase tracking-[0.16em] text-[#8A8A82]">{g.orders.length} order{g.orders.length === 1 ? "" : "s"}</div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2 border-t border-white/10 pt-4">
                    {g.orders.map((o) => (
                      <div key={o.id} className="flex items-center justify-between gap-3">
                        <span className="font-body text-xs text-[#8A8A82] truncate">#{o.id.slice(0, 8).toUpperCase()} · {o.items.map((it) => it.name).join(", ")}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-body text-sm">{formatINR(o.total)}</span>
                          <span className="font-grotesk text-[9px] uppercase tracking-[0.14em] text-[#B87333] border border-[#B87333]/40 rounded-full px-2 py-0.5">{o.fulfillment_status || "processing"}</span>
                          <PurchaseOrderButton api={api} order={o} user={user} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-2">
                    <button
                      data-testid={`project-statement-${g.name.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => downloadStatement(g.name)}
                      disabled={statementDl === g.name}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.16em] hover:border-[#B87333] transition-colors disabled:opacity-50"
                    >
                      <Download size={12} /> {statementDl === g.name ? "Preparing…" : "Statement"}
                    </button>
                    <button
                      data-testid={`project-reorder-${g.name.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => reorderProject(g)}
                      disabled={reordering === g.name}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#F5F5F0] text-black px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.16em] hover:bg-[#B87333] transition-colors disabled:opacity-50"
                    >
                      <ShoppingBag size={12} /> {reordering === g.name ? "Adding…" : "Reorder"}
                    </button>
                    <button
                      data-testid={`project-view-${g.name.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => { setProjectFilter(g.name); document.querySelector('[data-testid=order-history-heading]')?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                      className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.16em] text-[#8A8A82] hover:text-[#B87333] transition-colors px-2"
                    >
                      View orders <ArrowUpRight size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-16 mb-6" id="your-orders" data-testid="order-history-heading">
          <h2 className="font-serif text-2xl md:text-3xl">Your orders <span className="text-[#8A8A82] text-lg">({orders.length})</span></h2>
          <Link to="/shop" data-testid="shop-link" className="rounded-full border border-white/20 px-5 py-2.5 font-grotesk text-xs uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">Shop materials</Link>
        </div>
        {orders.length > 0 && groupByProject(orders).length > 1 && (
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="order-project-filter">
            {["All", ...groupByProject(orders).map((g) => g.name)].map((name) => (
              <button
                key={name}
                data-testid={`order-filter-${name.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setProjectFilter(name)}
                className={`rounded-full px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.14em] transition-colors ${projectFilter === name ? "bg-[#B87333] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}
              >
                {name}
              </button>
            ))}
          </div>
        )}
        {orders.length === 0 ? (
          <p className="font-body text-[#8A8A82]" data-testid="no-orders">No orders yet. Browse the store to place your first bulk order.</p>
        ) : (
          <div className="space-y-4" data-testid="order-history">
            {orders.filter((o) => projectFilter === "All" || ((o.project_name || "").trim() || "Unassigned") === projectFilter).map((o) => (
              <div key={o.id} data-testid="order-row" className="border border-white/10 rounded-sm p-6 hover:border-white/20 transition-colors">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-xl">{formatINR(o.total)}</span>
                    <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] rounded-full px-3 py-1 border border-white/15 text-[#C9C9C2]">{o.payment_status === "paid" ? "Paid" : o.payment_status === "awaiting_gateway" ? "Payment pending" : o.payment_status}</span>
                    <span data-testid="order-fulfillment" className="font-grotesk text-[10px] uppercase tracking-[0.18em] rounded-full px-3 py-1 border border-[#B87333]/40 text-[#B87333]">{o.fulfillment_status || "processing"}</span>
                    {o.project_name && <span data-testid="order-project" className="inline-flex items-center gap-1 font-grotesk text-[10px] uppercase tracking-[0.18em] rounded-full px-3 py-1 border border-white/15 text-[#8A8A82]"><FolderKanban size={11} /> {o.project_name}</span>}
                  </div>
                  <span className="font-body text-xs text-[#8A8A82]">#{o.id.slice(0, 8).toUpperCase()} · {new Date(o.created_at).toLocaleDateString()}</span>
                </div>
                <div className="font-body text-sm text-[#8A8A82] mt-3">{o.items.map((it) => `${it.quantity}× ${it.name}`).join(" · ")}</div>
                <div className="mt-5 mb-1"><OrderTimeline order={o} /></div>
                <OrderRetryPay api={api} order={o} onPaid={loadOrders} />
                <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-white/10">
                  <span className="font-body text-xs text-[#8A8A82]">
                    {o.tracking_number ? <>Tracking: <span className="text-[#B87333]">{o.tracking_courier} · {o.tracking_number}</span></> : "Tracking details will appear once dispatched."}
                  </span>
                  <button data-testid={`invoice-${o.id}`} onClick={() => downloadInvoice(o.id)} className="rounded-full border border-white/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">Download invoice</button>
                  <OrderClaimButton api={api} order={o} onSubmitted={loadClaims}/>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-16">
          <MyBuyerQuotations api={api} />
        </div>

        <div className="flex items-center justify-between mt-16 mb-6">
          <h2 className="font-serif text-2xl md:text-3xl">Your quote requests <span className="text-[#8A8A82] text-lg">({quotes.length})</span></h2>
          <Link to="/request-quote" data-testid="new-quote-link" className="rounded-full border border-white/20 px-5 py-2.5 font-grotesk text-xs uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">New request</Link>
        </div>
        {quotes.length === 0 ? (
          <p className="font-body text-[#8A8A82]" data-testid="no-quotes">No quote requests yet. <Link to="/request-quote" className="text-[#B87333] hover:underline">Request a quote</Link> and track its status here.</p>
        ) : (
          <div className="space-y-4" data-testid="quote-history">
            {quotes.map((q) => (
              <div key={q.id} data-testid="quote-row" className="border border-white/10 rounded-sm p-6 hover:border-white/20 transition-colors">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] border border-[#B87333]/40 rounded-full px-3 py-1">{q.ref}</span>
                    <span className="font-serif text-xl">{q.material}</span>
                    <StatusPill status={q.status} testid={`quote-req-status-${q.id}`} />
                  </div>
                  <span className="font-body text-xs text-[#8A8A82]">{new Date(q.created_at).toLocaleString()}</span>
                </div>
                <div className="font-body text-sm text-[#8A8A82] mt-3">
                  {q.grade ? `${q.grade} · ` : ""}{q.diameter ? `⌀ ${q.diameter} mm · ` : ""}{q.quantity} {q.unit || ""}
                  {q.delivery_state ? ` · ${q.delivery_state}` : ""}
                </div>
                {q.notes && <p className="font-body text-sm text-[#6f6f68] mt-2 leading-relaxed">{q.notes}</p>}
                {q.status !== "closed" && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <button
                      data-testid={`quote-add-cart-${q.id}`}
                      onClick={() => addQuoteToCart(q)}
                      disabled={converting === q.id}
                      className="inline-flex items-center gap-2 rounded-full bg-[#F5F5F0] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-50"
                    >
                      <ShoppingBag size={13} /> {converting === q.id ? "Adding…" : "Add to cart & checkout"}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-14 flex flex-wrap gap-4">
          <Link to="/request-quote" data-testid="account-request-quote" className="inline-flex items-center gap-3 rounded-full bg-[#F5F5F0] text-black px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors">
            Request a quote →
          </Link>
          {user?.role === "admin" && (
            <>
              <Link to="/admin/command" data-testid="go-executive-command"
                    className="group inline-flex items-center gap-3 rounded-full px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] text-black
                               bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333]
                               hover:shadow-[0_0_36px_rgba(216,178,62,0.55)] transition-shadow"
                    style={{ boxShadow: "0 0 22px rgba(216,178,62,0.35)" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-black/70 animate-pulse" />
                Executive Command
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link to="/admin" data-testid="go-admin"
                    className="inline-flex items-center gap-3 rounded-full border border-white/20 px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">
                Open Trade Desk →
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
